// The Meta and Google specialist boards' shared PLUMBING — queries, the
// channel narrowing, the alert evaluation and the ack split.
//
// ⚠️ THIS IS NOT "one parameterised surface". Scott's decision was separate
// pages sharing the COMPONENT SET, and that stands: each page owns its layout
// and its own channel panels. What is shared is the data path, because the
// alternative is two copies of the scoping rules — and the alert core has been
// corrected twice in the last fortnight, so a copy means the third correction
// gets made once and missed twice.
//
// ⚠️⚠️ THE CHANNEL IS APPLIED AT THE EVALUATION INPUT, never to its output. See
// channelScope.ts: `evaluateAlerts` returns a FLAT list over both channels, so
// filtering afterwards would leave the other channel reachable through
// `healthByAccount`, the status popovers and anything added later. Narrowing
// the input means it is never in the page's state at all.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { presetToRange, type DateRange } from '../../lib/reportingDateRange';
import { comparisonRange } from '../../lib/advertisingHome/comparisonRange';
import {
  EMPTY_HOME_FILTER,
  amFilterOptions,
  filterBookAccounts,
  filterRowsToAccounts,
  type HomeFilterSelection,
} from '../../lib/advertisingHome/bookFilter';
import { evaluateAlerts, type HomeAlert } from '../../lib/advertisingHome/alerts';
import { BrandAverages } from '../../lib/advertisingHome/noLeads';
import {
  scopeAccountsToChannel,
  scopeAsOfToChannel,
  scopeSpineToChannel,
} from '../../lib/advertisingHome/channelScope';
import { indexSpine } from '../../lib/advertisingHome/spine';
import { buildBookRows, sortBookRows, type BookSortKey } from '../../lib/advertisingHome/bookRows';
import { buildPulse } from '../../lib/advertisingHome/pulse';
import { rankCplChanges } from '../../lib/advertisingHome/cplChange';
import {
  applyAcknowledgements,
  type AcknowledgedAlert,
} from '../../lib/advertisingHome/acknowledgements';
import { groupNotesByAlert } from '../../lib/advertisingHome/alertNotes';
import type {
  AdChannel,
  ComparisonMode,
  HomeAccount,
  MetaLeadBreakdown,
} from '../../types/advertisingHome';
import type { ReportingDatePreset } from '../../types/reporting';
import type { PulseMetric } from '../../lib/advertisingHome/pulseSeries';
import { useCurrentUser } from '../auth';
import { patchMyPreferences } from '../accounts';
import {
  SPECIALIST_HOME_GRID_KEYS,
  decodeSpecialistViewState,
  encodeSpecialistViewState,
  type SpecialistViewState,
} from './homePrefs';
import {
  acknowledgeAlert,
  createAlertNote,
  getHomeBook,
  getHomeOverview,
  getMetaLeads,
  listAcknowledgements,
  listAlertNotes,
  unacknowledgeAlert,
} from './api';

/** The specialist pages open on the last 30 days, like the AM board. */
const DEFAULT_PRESET: ReportingDatePreset = 'past_30';

/**
 * ⚠️⚠️ THE SPECIALIST SCOPE IS THE WHOLE ROSTER, PASSED EXPLICITLY.
 *
 * `resolveScope` defaults to the requester's own book, which is right for an AM
 * and meaningless for a specialist — they have no book. So these pages send
 * `scope=all` rather than relying on a default.
 *
 * ⚠️ DO NOT "simplify" this by changing `resolveScope`'s default. Advertising
 * Home depends on it, and a changed default would silently move every AM's home
 * page to the whole roster. (Its own default IS covered —
 * service.test.ts asserts it and the mutation fails — but that test exists to
 * protect the AM page, not to license changing it.)
 */
export const SPECIALIST_SCOPE = 'all';

export interface SpecialistBoard {
  channel: AdChannel;
  scope: string;
  setScope: (s: string) => void;
  preset: ReportingDatePreset;
  setPreset: (p: ReportingDatePreset) => void;
  custom: DateRange;
  setCustom: (r: DateRange) => void;
  comparisonMode: ComparisonMode;
  setComparisonMode: (m: ComparisonMode) => void;
  filterSel: HomeFilterSelection;
  setFilterSel: (f: HomeFilterSelection) => void;
  range: DateRange;
  compare: DateRange;

  overviewQuery: ReturnType<typeof useQuery<Awaited<ReturnType<typeof getHomeOverview>>>>;
  bookQuery: ReturnType<typeof useQuery<Awaited<ReturnType<typeof getHomeBook>>>>;

  /** The scoped, filtered book — accounts that run THIS channel. */
  accounts: ReturnType<typeof scopeAccountsToChannel>;
  /**
   * The channel-scoped book BEFORE the filter row — what the filter bar's own
   * option lists are built from. ⚠️ Not `accounts`: an option list derived from
   * the already-filtered set would delete the options you would need to widen
   * the selection again.
   */
  channelBook: HomeAccount[];
  /** The AM filter's options for this board, "Unassigned" included. */
  amOptions: Array<{ id: string; name: string }>;
  evaluation: ReturnType<typeof evaluateAlerts>;
  counts: { total: number; critical: number; attention: number; ontrack: number };
  visible: HomeAlert[];
  acknowledged: AcknowledgedAlert[];
  alertsByAccount: Map<string, HomeAlert[]>;
  notesByAlert: Map<string, Awaited<ReturnType<typeof listAlertNotes>>>;
  bookRows: ReturnType<typeof buildBookRows>;
  /**
   * §11d — the Meta lead breakdown, keyed by account. EMPTY on the Google
   * board (the query is disabled there) and while the read is in flight, so the
   * Meta board can gate its extra columns on the data having ARRIVED rather than
   * on which board it is — columns turned on without rows print em-dashes,
   * which is the §9c defect again.
   */
  metaLeadsByAccount: Map<string, MetaLeadBreakdown[]>;
  pulse: ReturnType<typeof buildPulse>;
  winsWorries: ReturnType<typeof rankCplChanges>;
  asOf: { meta: string | null; google: string | null };
  nameOf: (id: string) => string;
  /**
   * ⚠️ TAKES AN ACCOUNT ID, NOT A UID — it resolves the account's manager
   * and then that uid's name. The parameter was named `uid` here while
   * every caller passed an account id; a reader who believed the name
   * would pass a uid and get null.
   */
  amNameOf: (accountId: string | null) => string | null;
  bookCurrency: string | null;

  onAcknowledge: (a: HomeAlert) => void;
  onUnacknowledge: (e: AcknowledgedAlert) => void;
  onAddNote: (a: HomeAlert, body: string) => void;
  busy: boolean;
  noteBusy: boolean;

  sortKey: BookSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: BookSortKey) => void;

  /** The pulse's plotted metric — persisted PER BOARD (see homePrefs). */
  pulseMetric: PulseMetric;
  setPulseMetric: (m: PulseMetric) => void;
}

export function useSpecialistBoard(channel: AdChannel): SpecialistBoard {
  const qc = useQueryClient();
  const user = useCurrentUser();

  const [scope, setScope] = useState<string>(SPECIALIST_SCOPE);
  const [preset, setPreset] = useState<ReportingDatePreset>(DEFAULT_PRESET);
  const [custom, setCustom] = useState<DateRange>(() => presetToRange(DEFAULT_PRESET, new Date()));
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('previous');
  const [filterSel, setFilterSel] = useState<HomeFilterSelection>(EMPTY_HOME_FILTER);
  const [sortKey, setSortKey] = useState<BookSortKey>('attention');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ⚠️ SEEDED LAZILY FROM THE RESOLVED PREFS, and written only on a real user
  // change — the save-on-change-only invariant the whole team-default model
  // rests on. An effect that re-seeded on every /me refetch would also stomp a
  // selection whose save was still in flight.
  const gridKey = SPECIALIST_HOME_GRID_KEYS[channel];
  const [view, setView] = useState<SpecialistViewState>(() =>
    decodeSpecialistViewState(user.gridPreferences?.[gridKey]),
  );
  const saveView = useMutation({
    mutationFn: (next: SpecialistViewState) =>
      patchMyPreferences(gridKey, encodeSpecialistViewState(next)),
    onSuccess: (u) => qc.setQueryData(['me'], u),
  });
  const setPulseMetric = (pulseMetric: PulseMetric): void => {
    const next = { ...view, pulseMetric };
    setView(next);
    saveView.mutate(next, {
      onError: (err) => console.error('Failed to save view preferences', err),
    });
  };

  // ⚠️ A LAZY INITIALISER, and the UTC date rather than the browser's own — the
  // complete-day bound must not shift underneath a tab left open across UTC
  // midnight, and a UTC+10 browser's date would put the bound on a day UTC has
  // not finished. Same posture as the AM page; see AlertInputs.utcToday.
  const [utcToday] = useState(() => new Date().toISOString().slice(0, 10));

  const range = useMemo(() => {
    try {
      return presetToRange(preset, new Date(), custom);
    } catch {
      return custom;
    }
  }, [preset, custom]);
  const compare = useMemo(() => comparisonRange(range, comparisonMode), [range, comparisonMode]);
  const rangeValid = Boolean(range.from && range.to && range.from <= range.to);

  // ⚠️ THE SAME query keys the AM board uses, so a specialist opening their page
  // after an AM has had theirs open pays for no second read of the same scope.
  const overviewQuery = useQuery({
    queryKey: ['advertising-home-overview', scope],
    queryFn: () => getHomeOverview(scope),
    staleTime: 5 * 60 * 1000,
  });
  const bookQuery = useQuery({
    queryKey: ['advertising-home-book', scope, range.from, range.to, compare.from, compare.to],
    queryFn: () => getHomeBook(scope, range, compare),
    enabled: rangeValid,
    staleTime: 5 * 60 * 1000,
  });
  const acksQuery = useQuery({
    queryKey: ['advertising-home-acks'],
    queryFn: listAcknowledgements,
    staleTime: 60 * 1000,
  });
  /**
   * §11d / §11e — META ONLY, and gated on the channel so the Google board
   * never pays for a read it cannot show. A companion to the spine rather than
   * a widening of it: see `buildMetaLeadBreakdownQuery` for why `SpineDay` was
   * left alone.
   */
  const metaLeadsQuery = useQuery({
    queryKey: ['advertising-home-meta-leads', scope, range.from, range.to],
    queryFn: () => getMetaLeads(scope, range),
    enabled: channel === 'meta' && rangeValid,
    staleTime: 5 * 60 * 1000,
  });

  const notesQuery = useQuery({
    queryKey: ['advertising-home-alert-notes'],
    queryFn: listAlertNotes,
    staleTime: 60 * 1000,
  });

  const invalidateAcks = (): void => {
    void qc.invalidateQueries({ queryKey: ['advertising-home-acks'] });
  };
  const ackMutation = useMutation({ mutationFn: acknowledgeAlert, onSuccess: invalidateAcks });
  const unackMutation = useMutation({ mutationFn: unacknowledgeAlert, onSuccess: invalidateAcks });
  const noteMutation = useMutation({
    mutationFn: createAlertNote,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['advertising-home-alert-notes'] });
    },
  });

  const overview = overviewQuery.data;
  const book = bookQuery.data;

  // ── The channel narrowing, applied ONCE, before anything reads the data ──
  const filteredAccounts = useMemo(
    () => filterBookAccounts(overview?.accounts ?? [], filterSel),
    [overview, filterSel],
  );
  const accounts = useMemo(
    () => scopeAccountsToChannel(filteredAccounts, channel),
    [filteredAccounts, channel],
  );
  /**
   * The channel-scoped book with the filter row NOT applied — the filter bar's
   * options come from here.
   *
   * ⚠️ CHANNEL-SCOPED RATHER THAN THE WHOLE ROSTER: on the Meta board, an AM
   * whose accounts all run Google only would otherwise appear in the dropdown
   * and select an empty board — an option that can only disappoint.
   */
  const channelBook = useMemo(
    () => scopeAccountsToChannel(overview?.accounts ?? [], channel),
    [overview, channel],
  );
  const overviewRows = useMemo(
    () => scopeSpineToChannel(filterRowsToAccounts(overview?.rows ?? [], accounts), channel),
    [overview, accounts, channel],
  );
  const asOf = useMemo(
    () => scopeAsOfToChannel(overview?.asOf ?? { meta: null, google: null }, channel),
    [overview, channel],
  );
  const bookRangeRows = useMemo(
    () => scopeSpineToChannel(filterRowsToAccounts(book?.rows ?? [], accounts), channel),
    [book, accounts, channel],
  );
  const bookCompareRows = useMemo(
    () => scopeSpineToChannel(filterRowsToAccounts(book?.compareRows ?? [], accounts), channel),
    [book, accounts, channel],
  );

  const spine = useMemo(() => indexSpine(overviewRows), [overviewRows]);
  const evaluation = useMemo(
    () =>
      evaluateAlerts({
        accounts,
        spine,
        asOf,
        utcToday,
        // Pacing is the AM page's opt-in toggle and is deliberately not carried
        // here: a specialist does not own the budget conversation.
        includePacing: false,
        brandAverages: new BrandAverages(overview?.brandWindows),
      }),
    [accounts, spine, asOf, utcToday, overview],
  );

  const counts = useMemo(() => {
    const c = { total: 0, critical: 0, attention: 0, ontrack: 0 };
    for (const health of evaluation.healthByAccount.values()) {
      c.total += 1;
      c[health] += 1;
    }
    return c;
  }, [evaluation]);

  // ⚠️ `'specialist'` — THIS BOARD. An alert the AM has cleared is still
  // outstanding here until a specialist clears it too, and vice versa. That is
  // intended, and it is why the page carries a line of copy saying so.
  const ackSplit = useMemo(
    () => applyAcknowledgements(evaluation.alerts, acksQuery.data ?? [], Date.now(), 'specialist'),
    [evaluation.alerts, acksQuery.data],
  );

  const alertsByAccount = useMemo(() => {
    const map = new Map<string, HomeAlert[]>();
    for (const a of evaluation.alerts) {
      const list = map.get(a.accountId);
      if (list) list.push(a);
      else map.set(a.accountId, [a]);
    }
    return map;
  }, [evaluation]);

  const notesByAlert = useMemo(
    () => groupNotesByAlert(notesQuery.data ?? []),
    [notesQuery.data],
  );

  /**
   * ⚠️ NARROWED TO THE BOARD'S OWN ACCOUNTS, like every other section — the
   * endpoint is scoped to the roster; the filter row is applied here.
   */
  const metaLeadsByAccount = useMemo(() => {
    const map = new Map<string, MetaLeadBreakdown[]>();
    const inScope = new Set(accounts.map((a) => a.id));
    for (const row of metaLeadsQuery.data?.rows ?? []) {
      if (!inScope.has(row.accountId)) continue;
      const list = map.get(row.accountId);
      if (list) list.push(row);
      else map.set(row.accountId, [row]);
    }
    return map;
  }, [metaLeadsQuery.data, accounts]);

  const bookRows = useMemo(() => {
    if (!overview || !book) return [];
    return sortBookRows(
      buildBookRows({
        accounts,
        rangeRows: bookRangeRows,
        compareRows: bookCompareRows,
        healthByAccount: evaluation.healthByAccount,
        metaLeadsByAccount,
        brandAverages: new BrandAverages(book.brandWindows),
      }),
      sortKey,
      sortDir,
    );
  }, [
    overview,
    book,
    accounts,
    bookRangeRows,
    bookCompareRows,
    evaluation,
    metaLeadsByAccount,
    sortKey,
    sortDir,
  ]);

  const pulse = useMemo(() => buildPulse(overviewRows, asOf), [overviewRows, asOf]);
  const winsWorries = useMemo(
    () => rankCplChanges(bookRangeRows, bookCompareRows),
    [bookRangeRows, bookCompareRows],
  );

  const bookCurrency = useMemo(() => {
    const spendBy = new Map<string, number>();
    for (const r of overviewRows) spendBy.set(r.currency, (spendBy.get(r.currency) ?? 0) + r.spend);
    let best: string | null = null;
    let max = -Infinity;
    for (const [c, amount] of spendBy) {
      if (amount > max) {
        max = amount;
        best = c;
      }
    }
    return best;
  }, [overviewRows]);

  /**
   * ⚠️ EXCLUDE-SELF: the options are built from the book narrowed by the OTHER
   * filters but NOT by the AM selection itself, so picking one manager does not
   * delete every other manager from the list you would need to change your mind.
   */
  const amOptions = useMemo(() => {
    const names = new Map((overview?.amOptions ?? []).map((o) => [o.uid, o.name]));
    return amFilterOptions(
      filterBookAccounts(channelBook, { ...filterSel, accountManagerIds: [] }),
      names,
    );
  }, [channelBook, filterSel, overview]);

  const nameOf = useMemo(() => {
    const byId = new Map((overview?.accounts ?? []).map((a) => [a.id, a.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [overview]);

  /**
   * ⚠️ WHO TO HAND IT BACK TO. An AM's board has implicit ownership; a
   * specialist looking at 250 accounts does not, and handing back is their most
   * common action. Display-only — `amOptions` is already on the overview
   * payload, so this costs no query.
   *
   * ⚠️ An account with NO manager, or a uid that resolves to nobody, returns
   * null and the row renders "Unassigned" — never a raw uid dressed as a name.
   */
  const amNameOf = useMemo(() => {
    const byUid = new Map((overview?.amOptions ?? []).map((o) => [o.uid, o.name]));
    const accountAm = new Map((overview?.accounts ?? []).map((a) => [a.id, a.accountManagerId]));
    return (accountId: string | null): string | null => {
      if (!accountId) return null;
      const uid = accountAm.get(accountId);
      if (!uid) return null;
      return byUid.get(uid) ?? null;
    };
  }, [overview]);

  const onSort = (key: BookSortKey): void => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'attention' ? 'asc' : 'desc');
    }
  };

  return {
    channel,
    scope,
    setScope,
    preset,
    setPreset,
    custom,
    setCustom,
    comparisonMode,
    setComparisonMode,
    filterSel,
    setFilterSel,
    range,
    compare,
    overviewQuery,
    bookQuery,
    accounts,
    channelBook,
    amOptions,
    evaluation,
    counts,
    visible: ackSplit.visible,
    acknowledged: ackSplit.acknowledged,
    alertsByAccount,
    notesByAlert,
    bookRows,
    metaLeadsByAccount,
    pulse,
    winsWorries,
    asOf,
    nameOf,
    amNameOf,
    bookCurrency,
    onAcknowledge: (a) =>
      ackMutation.mutate({
        accountId: a.accountId,
        channel: a.channel,
        alertType: a.type,
        // ⚠️ THIS BOARD's surface — never the channel, which is already above.
        surface: 'specialist',
        // The severity AS SHOWN: it is what a later escalation is measured
        // against, so it must be what was on screen when it was cleared.
        severity: a.severity,
      }),
    onUnacknowledge: (e) => unackMutation.mutate(e.acknowledgement.id),
    onAddNote: (a, body) =>
      noteMutation.mutate({
        accountId: a.accountId,
        channel: a.channel,
        alertType: a.type,
        body,
      }),
    busy: ackMutation.isPending || unackMutation.isPending,
    noteBusy: noteMutation.isPending,
    sortKey,
    sortDir,
    onSort,
    pulseMetric: view.pulseMetric,
    setPulseMetric,
  };
}
